// frontend/src/pages/portfolio/sections/HeroSection.tsx
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Download, Sparkles } from "lucide-react";
import { getMicrosoftStoreUrl } from "../../../utils/storeUrl";
import DashboardMockup from "../components/DashboardMockup";
import SectionEyebrow from "../components/SectionEyebrow";
import { container, sectionReveal } from "../components/styles";

const trustItems = [
  "Free forever",
  "No credit card",
  "Microsoft Store",
  "Privacy-first",
];

const HeroSection: React.FC = () => {
  return (
    <section
      className={`relative ${container} pb-16 pt-12 sm:pb-20 sm:pt-16 lg:pb-24 lg:pt-20`}
      aria-labelledby="hero-heading"
    >
      <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-16">
        <div className={`${sectionReveal} motion-safe:delay-75 motion-safe:[animation-duration:550ms]`}>
          <SectionEyebrow icon={Sparkles}>Workforce clarity</SectionEyebrow>
          <h1
            id="hero-heading"
            className="mt-4 font-display font-semibold tracking-tight text-primary-950"
            style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)", lineHeight: 1.05 }}
          >
            Employee time tracking your team can{" "}
            <span className="relative whitespace-nowrap">
              trust
              <span
                className="absolute -bottom-1 left-0 right-0 h-2.5 rounded-sm bg-gradient-to-r from-primary-300/70 to-primary-100/0 -z-10"
                aria-hidden
              />
            </span>
            —without the noise.
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-gray-600 sm:text-xl">
            A focused workspace for clocking in, reviewing activity, and
            keeping admins aligned.{" "}
            <strong className="font-medium text-gray-800">
              Free to use — pay only if you want AI summaries.
            </strong>
          </p>

          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
            <Link
              to="/register-org"
              className="btn-primary inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl px-6 py-3 text-base font-semibold min-h-[48px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              Start free
              <ArrowRight className="h-5 w-5" aria-hidden />
            </Link>
            <a
              href={getMicrosoftStoreUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl border border-primary-200 bg-white px-6 py-3 text-base font-semibold text-primary-900 shadow-xs hover:bg-primary-50 hover:border-primary-300 transition-colors min-h-[48px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 touch-manipulation"
            >
              <Download className="h-5 w-5" aria-hidden />
              Get the app
            </a>
          </div>

          <ul
            className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-primary-800/90 sm:flex sm:flex-wrap sm:gap-x-6"
            aria-label="Why teams choose Time Tracker"
          >
            {trustItems.map((item) => (
              <li key={item} className="flex items-center gap-2">
                <Check className="h-4 w-4 shrink-0 text-success-600" aria-hidden />
                <span className="font-medium">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={`relative ${sectionReveal} motion-safe:delay-150 motion-safe:[animation-duration:550ms]`}>
          <DashboardMockup />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
